import { Select, Stack, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { api, type DeliveryAgent } from "../lib/api";

/**
 * Who takes it out — the same control wherever a delivery is assigned.
 *
 * WHY IT IS SHARED
 *
 * Two people assign deliveries and they are in different modules: the
 * Billing Executive on the billing screen, and — since the business moved
 * service assignment — the Service Manager at the outcome step. Written twice
 * the two would drift, and the part that would drift is the copy: "leave it
 * for the Store Manager" is not decoration, it is the only thing on the
 * screen that says what a blank actually does.
 *
 * WHY A BLANK IS AN OPTION AND NOT AN OVERSIGHT
 *
 * The business chose "blank leaves it for the Store Manager" over defaulting
 * to whoever collected the battery. So the empty choice is offered
 * explicitly, and it is labelled with its consequence rather than left as an
 * unexplained empty row.
 *
 * WHAT THE HINT IS FOR
 *
 * `hint` is where a screen names the person the reader would probably pick —
 * the agent who collected this battery, say. It is shown next to the field
 * and NOT pre-selected, which is the whole point: the old behaviour picked
 * them automatically, and the business asked for the choice to be somebody's
 * decision. Naming them keeps the useful half.
 */
export function DeliveryAgentPicker({
  value,
  onChange,
  label = "Delivery agent",
  /** A sentence naming the obvious candidate. Not pre-selected — see above. */
  hint,
  /** What the reader should understand a blank to mean, if not the default. */
  blankMeans = "This will wait in the Store Manager's list rather than appearing on an agent's round.",
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: React.ReactNode;
  blankMeans?: string;
  disabled?: boolean;
}) {
  const [agents, setAgents] = useState<DeliveryAgent[]>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { agents: loaded } = await api.deliveryAgents();
        if (live) setAgents(loaded);
      } catch {
        // A list that will not load must not block the step it sits in. The
        // job can still be completed and the bill can still be raised; the
        // delivery simply waits for a manager, and the message below says so
        // rather than leaving an empty dropdown to be interpreted.
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <Stack gap={6}>
      <Select
        id="delivery-agent"
        label={label}
        value={value}
        disabled={disabled}
        onChange={(next) => onChange(next ?? "")}
        allowDeselect={false}
        searchable
        nothingFoundMessage="No agent by that name"
        data={[
          { value: "", label: "Leave for the Store Manager to assign" },
          ...(agents ?? []).map((agent) => ({
            value: agent.id,
            // A plain Delivery Agent needs no qualifier; anybody else in this
            // list is covering a round, and saying so is the difference
            // between a name and a decision.
            label:
              agent.role === "DELIVERY_AGENT"
                ? agent.name
                : `${agent.name} (${agent.role.replace(/_/g, " ").toLowerCase()})`,
          })),
        ]}
      />
      {hint && (
        <Text fz="sm" c="gray.6">
          {hint}
        </Text>
      )}
      {failed && (
        <Text fz="sm" c="gray.6">
          The list of agents could not be loaded. Carry on — the Store Manager
          can assign the delivery afterwards.
        </Text>
      )}
      {!failed && agents?.length === 0 && (
        <Text fz="sm" c="gray.6">
          Nobody currently holds Delivery access. Carry on — the delivery will
          wait until somebody does.
        </Text>
      )}
      {!value && <Text fz="sm" c="gray.6">{blankMeans}</Text>}
    </Stack>
  );
}
